import { fetch, getHeaders, makeRequest } from "$utils/utils";
import config from "$services/config";
import { queryClient } from "$utils/queryClient";
import { OpService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

const { shipping_api_url: shipping_url } = config;

export const getShippingRecords = async (params) => {
  try {
    const { data } = (await fetch(`/shippingRecords`, {}, shipping_url)) as any;

    let returnData = data;

    if (params.$notStatus) {
      returnData = returnData.filter((d) => d.status !== params.$notStatus);
    }

    return returnData;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const getShipmentsWithPopulateOps = async (filters) => {
  try {
    // Handle both status and $inStatus keys
    const { status } = filters;
    const { $inStatus } = filters;
    const statuses = status ? [status] : $inStatus;

    const { data } = await fetch(
      "/shippingRecords",
      { params: { statuses, withTotalParcel: true } },
      shipping_url
    );

    let shipping;

    if (filters.$inStatus) {
      shipping = data.filter((shippingRecords) =>
        filters.$inStatus.includes(shippingRecords.status)
      );
    }
    if (filters.status) {
      shipping = data.filter(
        (shippingRecords) => shippingRecords.status === filters.status
      );
    }

    const opList = [
      ...new Set(
        shipping
          ?.map((shippingRecord) =>
            shippingRecord?.shipments?.map((shipment) => shipment.opId)
          )
          ?.flat()
      ),
    ];

    let selectedOps;
    selectedOps = await queryClient.getQueryData([
      CacheInvalidationAction.GET_OPS,
      opList,
    ]);
    if (!selectedOps) {
      const chunkTotal = [];
      const opsTotal = [];
      for (let i = 0; i <= opList.length; i += 50) {
        chunkTotal.push(opList.slice(i, i + 50));
      }

      // eslint-disable-next-line no-restricted-syntax
      for await (const chunk of chunkTotal) {
        const ops = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_OPS, chunk],
          () =>
            OpService.getOps({
              $inId: chunk,
            }),
          { cacheTime: 5 * 60 * 1000 }
        );
        opsTotal.push(ops.data);
      }
      selectedOps = opsTotal.flat();
    }
    await queryClient.setQueryData(
      [CacheInvalidationAction.GET_OPS, opList],
      selectedOps
    );

    shipping = shipping.map((shippingRecord) => ({
      ...shippingRecord,
      shipments: shippingRecord.shipments.map((shipment) => ({
        ...shipment,
        op: selectedOps.find((op) => op?._id === shipment.opId),
      })),
    }));

    if (filters?.company) {
      shipping = shipping.filter(
        (shippingRecord) =>
          shippingRecord.shipments.length === 0 ||
          shippingRecord.shipments.some(
            (shipment) => shipment.op?.company?._id === filters?.company
          )
      );
    }

    return shipping.sort((a, b) => a.code.localeCompare(b.code));
  } catch (error) {
    console.error(error);
  }
};

export const getShippingRecord = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${shipping_url}/shippingRecords/${id}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data;
  } catch (error: any) {
    return { error };
  }
};

export const updateShippingRecord = async (id: string, body: any) => {
  try {
    const res = await fetch(
      `/updateShippingRecordAndOp/${id}`,
      { method: "PUT", data: body },
      shipping_url
    );

    return res.data;
  } catch (error: any) {
    return { error };
  }
};

export const createShippingRecord = async (body: any) => {
  try {
    const res = await fetch(
      "/shippingRecords",
      { method: "POST", data: body },
      shipping_url
    );

    return res.data;
  } catch (error: any) {
    console.error("Error creating shipping record:", error);
    throw error;
  }
};

export const deleteShippingRecord = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${shipping_url}/shippingRecords/${id}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    return res.data;
  } catch (error: any) {
    return { error };
  }
};

export const removeShipmentFromShippingRecord = async (
  shippingRecordId: string,
  shipmentId: string
) => {
  const res = await fetch(
    `/shippingRecords/${shippingRecordId}/shipments/${shipmentId}`,
    { method: "DELETE" },
    shipping_url
  );
  return res.data;
};
